import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { AdminPageHeader, Field, Select, Toggle } from "./_helpers";
import { toast } from "sonner";
import { Search, Film, X, Link as LinkIcon } from "lucide-react";

/**
 * AdminVideos
 * -----------
 * Yeni özellikler:
 *  • Video'nun yayından kalma süresi (expire_at)
 *  • Admin il filtresi + ilin haber listesi: bir haberi tıklayınca
 *    - başlık/spot/kapak alanlarını kopyalar
 *    - haberin kendi video_url'i varsa onu YouTube URL alanına kopyalar
 *  • Public videos listesi zaten video'lu haberleri OTOMATİK birleştiriyor
 *    (bu sayfadan ekstra bir şey yapmaya gerek yok). Buradaki haber seçimi,
 *    "video'yu kendi başlığıyla, kapağıyla, yayın bitiş tarihiyle" vitrine
 *    çıkarmak isteyenler için.
 */

const emptyForm = {
  title: "",
  description: "",
  youtube_url: "",
  province_id: "",
  category_id: "",
  news_id: "",
  thumbnail: "",
  publish_at: "",
  expire_at: "",
  is_active: true,
  is_featured: false,
};

export default function AdminVideos() {
  const [items, setItems] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cats, setCats] = useState([]);
  const [f, setF] = useState(emptyForm);
  const [newsItems, setNewsItems] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsQ, setNewsQ] = useState("");
  const [onlyVideoNews, setOnlyVideoNews] = useState(true);

  const load = () =>
    api
      .get("/videos?limit=200&include_news=false")
      .then((r) => setItems(r.data.items));

  useEffect(() => {
    load();
    api.get("/provinces?active_only=false").then((r) => setProvinces(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  // Load news for the picker whenever the admin filters change.
  useEffect(() => {
    setNewsLoading(true);
    const params = new URLSearchParams({ admin: "true", limit: "100" });
    if (f.province_id) params.set("province_id", f.province_id);
    if (newsQ.trim()) params.set("search", newsQ.trim());
    api
      .get(`/news?${params}`)
      .then((r) => {
        let arr = r.data.items || [];
        if (onlyVideoNews) arr = arr.filter((n) => !!n.video_url);
        setNewsItems(arr);
      })
      .catch(() => setNewsItems([]))
      .finally(() => setNewsLoading(false));
  }, [f.province_id, newsQ, onlyVideoNews]);

  const save = async () => {
    if (!f.youtube_url?.trim()) return toast.error("YouTube URL zorunlu");
    if (!f.title?.trim()) return toast.error("Başlık zorunlu");
    try {
      await api.post("/videos", {
        ...f,
        province_id: f.province_id || null,
        category_id: f.category_id || null,
        news_id: f.news_id || null,
        publish_at: f.publish_at || null,
        expire_at: f.expire_at || null,
      });
      setF(emptyForm);
      load();
      toast.success("Eklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    }
  };

  const pickNews = (n) => {
    setF((prev) => ({
      ...prev,
      news_id: n.id,
      title: n.title || prev.title,
      description: n.spot || prev.description,
      youtube_url: n.video_url || prev.youtube_url,
      province_id: n.province_id || prev.province_id,
      category_id: n.category_id || prev.category_id,
      thumbnail: n.cover_image || prev.thumbnail,
    }));
    toast.success(n.video_url ? "Haber + video yüklendi" : "Haber bilgileri yüklendi");
  };

  const clearNewsLink = () => {
    setF((prev) => ({ ...prev, news_id: "" }));
  };

  const selectedNews = useMemo(
    () => newsItems.find((n) => n.id === f.news_id),
    [newsItems, f.news_id]
  );

  return (
    <div>
      <AdminPageHeader
        title="Video Yönetimi"
        subtitle="Video'lu haberler Video sayfasına otomatik düşer. Burada eklenenler ekstra vitrindir."
      />
      <div className="grid lg:grid-cols-3 gap-4">
        {/* ------- Left: existing videos grid ------- */}
        <div className="lg:col-span-2 bg-white border border-gray-100 rounded-lg p-4">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <Film className="w-4 h-4" /> Vitrindeki Videolar ({items.length})
          </h3>
          {items.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center">
              Henüz özel video eklenmedi. Video'lu haberler zaten public sayfada otomatik görünüyor.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {items.map((v) => (
                <div key={v.id} className="relative group" data-testid={`admin-video-${v.id}`}>
                  <img
                    src={v.thumbnail}
                    alt={v.title}
                    className="w-full aspect-video object-cover rounded"
                  />
                  <div className="text-xs mt-1 line-clamp-2 font-medium">{v.title}</div>
                  {v.expire_at && (
                    <div className="text-[10px] text-gray-500">
                      Bitiş: {new Date(v.expire_at).toLocaleDateString("tr-TR")}
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      await api.delete(`/videos/${v.id}`);
                      load();
                    }}
                    className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded opacity-80 hover:opacity-100"
                    data-testid={`admin-video-delete-${v.id}`}
                  >
                    Sil
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ------- Right: new video form ------- */}
        <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-3" data-testid="admin-video-form">
          <h3 className="font-bold">Yeni Video</h3>

          {/* NEWS PICKER */}
          <div className="border border-dashed border-gray-200 rounded-md p-2 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                <LinkIcon className="w-3.5 h-3.5" /> Haberden Seç
              </div>
              {f.news_id && (
                <button
                  onClick={clearNewsLink}
                  className="text-[11px] text-gray-500 hover:text-[#e11d2a] inline-flex items-center gap-0.5"
                >
                  <X className="w-3 h-3" /> bağlantıyı kaldır
                </button>
              )}
            </div>

            {/* Province filter + search */}
            <div className="flex items-center gap-1.5 mb-2">
              <select
                value={f.province_id || ""}
                onChange={(e) => setF({ ...f, province_id: e.target.value })}
                className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white"
                data-testid="admin-video-news-province"
              >
                <option value="">Tüm İller</option>
                {provinces.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={onlyVideoNews}
                  onChange={(e) => setOnlyVideoNews(e.target.checked)}
                  className="w-3.5 h-3.5 accent-[#e11d2a]"
                />
                sadece video'lu
              </label>
            </div>
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={newsQ}
                onChange={(e) => setNewsQ(e.target.value)}
                placeholder="Haber başlığı ara..."
                className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded text-xs"
                data-testid="admin-video-news-search"
              />
            </div>

            <div className="max-h-40 overflow-y-auto bg-white border border-gray-100 rounded">
              {newsLoading ? (
                <div className="p-3 text-center text-xs text-gray-400">Yükleniyor...</div>
              ) : newsItems.length === 0 ? (
                <div className="p-3 text-center text-xs text-gray-400">Haber bulunamadı</div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {newsItems.slice(0, 50).map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => pickNews(n)}
                        className={`w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-gray-50 ${
                          f.news_id === n.id ? "bg-red-50 ring-1 ring-[#e11d2a]/40" : ""
                        }`}
                        data-testid={`admin-video-news-pick-${n.id}`}
                        title={n.title}
                      >
                        <span className="w-10 h-6 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                          {n.cover_image && (
                            <img
                              src={n.cover_image}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          )}
                        </span>
                        <span className="flex-1 min-w-0 text-[11px] truncate">{n.title}</span>
                        {n.video_url && (
                          <span className="text-[9px] font-bold text-white bg-[#e11d2a] px-1 rounded">
                            VİDEO
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {selectedNews && (
              <div className="mt-2 text-[11px] text-gray-600 bg-white border border-gray-100 rounded p-1.5">
                Seçili: <strong>{selectedNews.title}</strong>
              </div>
            )}
          </div>

          <Field
            label="YouTube URL *"
            value={f.youtube_url}
            onChange={(e) => setF({ ...f, youtube_url: e.target.value })}
          />
          <Field
            label="Başlık *"
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
          <Field
            label="Açıklama"
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
          <Select
            label="İl"
            value={f.province_id || ""}
            options={[
              { value: "", label: "Genel" },
              ...provinces.map((p) => ({ value: p.id, label: p.name })),
            ]}
            onChange={(e) => setF({ ...f, province_id: e.target.value })}
          />
          <Select
            label="Kategori"
            value={f.category_id || ""}
            options={[
              { value: "", label: "-" },
              ...cats.map((c) => ({ value: c.id, label: c.name })),
            ]}
            onChange={(e) => setF({ ...f, category_id: e.target.value })}
          />

          {/* Expire */}
          <div className="relative" data-testid="admin-video-expire-wrap">
            <label className="block text-xs font-semibold text-gray-700 mb-0.5">
              Yayından Kalma Tarihi
            </label>
            <input
              type="datetime-local"
              value={f.expire_at ? f.expire_at.slice(0, 16) : ""}
              onChange={(e) =>
                setF({
                  ...f,
                  expire_at: e.target.value ? new Date(e.target.value).toISOString() : "",
                })
              }
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
              data-testid="admin-video-expire"
            />
            {f.expire_at && (
              <button
                type="button"
                onClick={() => setF({ ...f, expire_at: "" })}
                className="absolute right-2 top-7 text-[11px] text-gray-400 hover:text-[#e11d2a]"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <p className="text-[11px] text-gray-500 mt-0.5">
              Bu tarihten sonra video public listede görünmez.
            </p>
          </div>

          <Toggle
            label="Öne çıkar"
            value={f.is_featured}
            onChange={(v) => setF({ ...f, is_featured: v })}
          />
          <Toggle
            label="Aktif"
            value={f.is_active}
            onChange={(v) => setF({ ...f, is_active: v })}
          />

          <button
            onClick={save}
            className="w-full px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold hover:bg-red-700"
            data-testid="admin-video-save"
          >
            Ekle
          </button>
        </div>
      </div>
    </div>
  );
}
